import type { CartCheckoutPanelProps } from '@engyalo/delivery-ui-components'
import type { DefaultProps } from '../../../interfaces'
import { TextLibrary } from '../defaultConstants'

/**
 * Default props for the read-only Entrega/Pago panel above the cart lines.
 *
 * Every value here is either a text token or an icon glyph — no data. The
 * two data props, `expectedDeliveryDate` and `paymentMethod`, are injected
 * by `getCartCheckoutPanelProps` in `useCinnamonProps.ts` from the session's
 * `checkoutRules` selections. Nothing here reads Redux or the session, on
 * purpose: this file is the fleet-wide baseline a storefront's SCS body
 * merges on top of.
 *
 * ⚠️ **Copy in `defaultTexts/*.json` is a baseline, not a mandate.** A
 * storefront that overrides `texts.cart.deliveryHelperText` with its own
 * copy still gets the row. A storefront that clears the label to `''` loses
 * the row entirely — `CartCheckoutPanel`'s `rowShows` guard drops any row
 * with a missing value *or* a missing label (COM-852). That is the intended
 * escape hatch for a storefront that wants to hide one of the two rows
 * without an SCS body change.
 *
 * ⚠️ **No `paymentMethodLabels`.** That map is a tenant's word for each
 * backend payment enum value (`{ CASH: 'Efectivo contra entrega' }`), so it
 * cannot ship as fleet-wide copy — a wrong entry here would rename every
 * storefront's payment method. The panel renders an unmapped method raw,
 * which is visible and diagnosable rather than hidden.
 */
export const CartCheckoutPanelDefault: DefaultProps<CartCheckoutPanelProps> = {
  deliveryLabel: TextLibrary.CART_DELIVERY_LABEL,
  deliveryHelperText: TextLibrary.CART_DELIVERY_HELPER_TEXT,
  paymentLabel: TextLibrary.CART_PAYMENT_LABEL,
  paymentHelperText: TextLibrary.CART_PAYMENT_HELPER_TEXT,
  /*
   * The empty-row prompts, bound as of `@engyalo/delivery-ui-components`
   * `1.24.0` (COM-1184 PR 5a, commerce-ui-components #500). A storefront
   * with no picker route for a rule still has no action to pair with the
   * prompt — the library's `rowShowsEmpty` AND-drops the row when either
   * half is missing — so these defaults are inert on an unopted-in
   * storefront and the "zero visual change" story holds.
   */
  deliveryEmptyLabel: TextLibrary.CART_DELIVERY_EMPTY_LABEL,
  paymentEmptyLabel: TextLibrary.CART_PAYMENT_EMPTY_LABEL,
  /*
   * The library's default is `MdInventory`, closest resolving stand-in for
   * a delivery truck since neither the `md` nor the `cm` curated set carries
   * one (see the panel's own note in its `defaultProps`). Duplicated here
   * rather than pulled from the library because cinnamon owns the token
   * seam — a tenant overriding the glyph does so on this prop, not by
   * shipping a new library version.
   */
  deliveryIcon: 'MdInventory',
  paymentIcon: 'MdOutlinePayments',
  iconset: 'md',
}

export default CartCheckoutPanelDefault
